import { Button, Divider, Icon, Modal, Segment } from 'semantic-ui-react';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { ModalProps } from '../../types/ModalProps';
import {
    StepConfig,
    StepValue,
    PublicationEditProgressBar,
    getPreviousStep,
    getNextStep,
} from './PublicationEditProgressBar';
import PublicationService, { Publication } from './PublicationService';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import ReactJson, { InteractionProps } from 'react-json-view';
import { AppContext } from '../../model/context/app/app';
import { ApiError, ConfirmChangesModal } from './ConfirmChangesModal';
import { PublicationView } from './PublicationView';
import { useTranslation } from 'react-i18next';

const publicationService = new PublicationService();

export const PublicationEditorModal: FunctionComponent<ModalProps> = ({ showModal = false, onClose }) => {
    const { t } = useTranslation();
    const [selectedPublication, setSelectedPublication] = useState<Publication | null>(null);
    const [selectedPubJSON, setSelectedPubJSON] = useState<any>(null);
    const [editedPublication, setEditedPublication] = useState<any | null>(null);
    const [activeStep, setActiveStep] = useState<StepValue>(StepValue.SELECT_PUBLICATION);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [errorOnSave, setErrorOnSave] = useState<ApiError | undefined>();

    const context = useContext(AppContext);
    const closeModal = () => {
        setConfirmOpen(false);
        setErrorOnSave(undefined);
        setSelectedPublication(null);
        setSelectedPubJSON(null);
        setEditedPublication(null);
        setActiveStep(StepValue.SELECT_PUBLICATION);
        onClose();
    };
    const onModification = (edit: InteractionProps) => {
        if (edit.new_value === 'error') {
            console.error('Invalid modification');
            return false;
        }
        setEditedPublication(edit.updated_src);
    };

    const closeConfirmSaveDialog = () => {
        setErrorOnSave(undefined);
        setConfirmOpen(false);
    };
    const loadPublication = () =>
        publicationService
            .publication(selectedPublication?.id, context.token)
            .then((value) => setSelectedPubJSON(value));

    const stepConfig: StepConfig[] = [
        { step: StepValue.SELECT_PUBLICATION, text: t('publicationEditor.steps.selectPublication.label') },
        {
            step: StepValue.EDIT_PUBLICATION,
            text: t('publicationEditor.steps.editPublication.label'),
            transitionBackOp: () => setEditedPublication(null),
        },
        { step: StepValue.VERIFY_EDITED_PUBLICATION, text: t('publicationEditor.steps.verifyPublication.label') },
    ];
    const savePublication = () => {
        publicationService.savePublication(editedPublication, context.token).then(
            (value) => {
                if (value) {
                    setErrorOnSave({ error: false });
                }
            },
            (rejected) => {
                console.error('Received error', rejected);
                setErrorOnSave({ error: true, message: rejected });
            }
        );
    };

    const applyBackTransitionsFunc = () => {
        const prevStep = getPreviousStep(activeStep);
        stepConfig[prevStep]?.transitionBackOp?.();
        setActiveStep(prevStep);
    };

    const isNextAllowed = () => {
        switch (activeStep) {
            case StepValue.SELECT_PUBLICATION:
                return selectedPublication !== null;
            case StepValue.EDIT_PUBLICATION:
                return editedPublication !== null;
            case StepValue.VERIFY_EDITED_PUBLICATION:
                return true;
        }
    };

    useEffect(() => {
        if (selectedPublication) {
            loadPublication();
        }
    }, [selectedPublication]);

    return (
        <Modal open={showModal} size={'fullscreen'} closeOnEscape={true} closeOnTriggerBlur={true} onClose={closeModal}>
            <Modal.Header>{t('publicationEditor.headline')}</Modal.Header>
            <Modal.Content scrolling={true}>
                <Segment basic>
                    {activeStep === StepValue.SELECT_PUBLICATION && (
                        <PublicationView
                            selectedPublication={selectedPublication}
                            onClick={setSelectedPublication}
                            onDoubleClick={() => setActiveStep(getNextStep(activeStep))}></PublicationView>
                    )}

                    {activeStep === StepValue.EDIT_PUBLICATION && (
                        <ReactJson
                            onEdit={onModification}
                            onAdd={onModification}
                            onDelete={onModification}
                            quotesOnKeys={false}
                            validationMessage={t('publicationEditor.steps.editPublication.validationError') || ''}
                            theme={'apathy:inverted'}
                            name={false}
                            src={editedPublication || selectedPubJSON || {}}
                            displayDataTypes={true}></ReactJson>
                    )}

                    {activeStep === StepValue.VERIFY_EDITED_PUBLICATION && (
                        <ReactDiffViewer
                            oldValue={JSON.stringify(selectedPubJSON, null, 2)}
                            newValue={JSON.stringify(editedPublication, null, 2)}
                            splitView={true}
                            compareMethod={DiffMethod.CHARS}
                            leftTitle={t('publicationEditor.steps.verifyPublication.old') || ''}
                            rightTitle={t('publicationEditor.steps.verifyPublication.new') || ''}
                        />
                    )}

                    <Divider horizontal hidden />

                    <div style={{ textAlign: 'center' }}>
                        <Button
                            onClick={applyBackTransitionsFunc}
                            disabled={activeStep === StepValue.SELECT_PUBLICATION}
                            size={'big'}
                            icon
                            labelPosition="left">
                            <Icon name="arrow left" />
                            {t('publicationEditor.back')}
                        </Button>

                        {activeStep !== StepValue.VERIFY_EDITED_PUBLICATION ? (
                            <Button
                                onClick={() => setActiveStep(getNextStep(activeStep))}
                                disabled={!isNextAllowed()}
                                size={'big'}
                                icon
                                labelPosition="right">
                                <Icon name="arrow right" />
                                {t('publicationEditor.next')}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => setConfirmOpen(true)}
                                disabled={false}
                                active={false}
                                size={'big'}
                                icon
                                labelPosition="right">
                                <Icon name="save" />
                                {t('publicationEditor.save')}
                            </Button>
                        )}
                    </div>

                    <ConfirmChangesModal
                        showModal={confirmOpen}
                        onClose={closeConfirmSaveDialog}
                        onAbort={closeConfirmSaveDialog}
                        error={errorOnSave}
                        onSubmit={savePublication}></ConfirmChangesModal>
                </Segment>

                <PublicationEditProgressBar activeStep={activeStep} stepConfig={stepConfig} />
            </Modal.Content>
        </Modal>
    );
};
